import React, { useEffect } from 'react'
import { ScrollView, View, Image, ImageStyle as RNImageStyle } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text, Button, Icon } from '@components/base'
import { topPriorityCoachAssignmentSelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { SurveysConfigKind, UserPropertyOnboardingExperienceKind } from '@src/types'
import { weightLossExperienceCompletedImage } from '@src/assets/images'
import { userSelector } from '@src/selectors/app'
import { NutritionistBubble } from '@src/components/NutritionistBubble'
import { AppStackParamList } from '@src/navigation/types'
import { useIsDarkMode } from '@src/config/theme'
import { Analytics, CustomEventTypes } from '@src/config'
import { useVideoCallProductType } from '@src/hooks/useVideoCallProductType'
import { LoadingContainer } from '../Common/containers/LoadingContainer'

export const WeightLossExperienceDesiredWeightCompletedScreen = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const insets = useSafeAreaInsets()
  const isDarkMode = useIsDarkMode()

  const coachAssignment = useSelector(topPriorityCoachAssignmentSelector)
  const nutritionist = coachAssignment?.coach
  const user = useSelector(userSelector)
  const { isHealthCoachingVideoCallProductType: isHealthCoaching } = useVideoCallProductType()

  const dispatch = useDispatch()

  useEffect(() => {
    if (nutritionist) {
      return
    }

    dispatch({
      type: 'nutritionistHub/fetchCoachAssignments',
    })
  }, [dispatch, nutritionist])

  const title = () => {
    switch (user?.onboardingExperience?.kind) {
      case UserPropertyOnboardingExperienceKind.WeightLoss:
        return 'Nutrisense creates long-term results'
      case UserPropertyOnboardingExperienceKind.WomensHealth:
        return 'Nutrisense supports lasting hormonal balance'
      case UserPropertyOnboardingExperienceKind.PreDiabetes:
        return 'Nutrisense helps you maintain consistent levels'
      case UserPropertyOnboardingExperienceKind.Other:
        return 'Nutrisense helps gain control over your nutrition!'
    }
  }
  const chatBubbleText = () => {
    if (isHealthCoaching) {
      return "You've got this — I'm here to help every step of the way."
    }

    switch (user?.onboardingExperience?.kind) {
      case UserPropertyOnboardingExperienceKind.WeightLoss:
        return "Let's personalize your weight loss journey together!"
      case UserPropertyOnboardingExperienceKind.WomensHealth:
        return 'Let’s personalize your women’s health journey together!'
      case UserPropertyOnboardingExperienceKind.PreDiabetes:
        return 'Let’s personalize your pre-diabetes journey together!'
      default:
        return 'Let’s personalize your plan together!'
    }
  }
  const renderBlurb = () => {
    // Health coaches are not allowed to provide clinical or 'personalized' advice.
    // We also need to be careful about any copy that may suggest otherwise.
    //
    const realisticBlurb = isHealthCoaching
      ? 'can be sustainable with the right habits'
      : 'is a realistic target!'

    if (user?.onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss) {
      return (
        <Text type="regular" style={styles.blurbText}>
          Losing{' '}
          <Text type="regular" style={styles.desiredWeightLoss}>
            {user.onboardingExperience.desiredWeightLossAmount}
          </Text>{' '}
          {user.onboardingExperience.realisticWeightLoss
            ? `lbs ${realisticBlurb}`
            : "lbs will be challenging but we'll tackle it together!"}
        </Text>
      )
    }

    const blurbText =
      user?.onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.PreDiabetes
        ? 'Better glucose control is a realistic goal!'
        : 'Feeling better is a realistic goal!'
    return (
      <Text type="regular" style={styles.blurbText}>
        {blurbText}
      </Text>
    )
  }

  useEffect(() => {
    if (
      !user?.onboardingExperience ||
      user.onboardingExperience.realisticWeightLoss === undefined
    ) {
      return
    }

    // Only Weight Loss users have the realistic event
    if (user.onboardingExperience.kind !== UserPropertyOnboardingExperienceKind.WeightLoss) {
      return
    }

    const event = user.onboardingExperience.realisticWeightLoss
      ? CustomEventTypes.OnboardingExperienceBasicInformationCompletedScreenRealistic
      : CustomEventTypes.OnboardingExperienceBasicInformationCompletedScreenChallenging
    Analytics.track(event)

    return
  }, [user?.onboardingExperience])

  if (!nutritionist) {
    return <LoadingContainer />
  }

  const { photo } = nutritionist.coachProfile || {}

  const handleOnContinue = () => {
    Analytics.track(CustomEventTypes.OnboardingExperienceBasicInformationCompletedScreenContinue)

    // Weight Loss users should go to the pace survey
    // eslint-disable-next-line no-nested-ternary
    const surveyToRedirect =
      user?.onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss
        ? SurveysConfigKind.OnboardingExperiencePace
        : SurveysConfigKind.OnboardingExperienceGoals
    navigation.replace('Questionnaire', {
      questionnaire: surveyToRedirect,
    })
  }

  return (
    <View style={styles.topLevelContainer}>
      <ScrollView
        bounces={false}
        contentContainerStyle={[styles.container, { paddingBottom: insets.bottom + 16 }]}
      >
        <View style={{ alignItems: 'center' }}>
          <Text type="title-1" style={styles.title}>
            {title()}
          </Text>

          <Image
            resizeMethod="resize"
            source={weightLossExperienceCompletedImage(isDarkMode).imageSource}
            style={styles.image as RNImageStyle}
          />

          {renderBlurb()}

          <NutritionistBubble photoSrc={photo} label={chatBubbleText()} />
        </View>

        <Button
          type="primary"
          size="block"
          onPress={handleOnContinue}
          accessibilityLabel="Complete the questionnaire"
          accessoryRight={<Icon name="arrow-right" />}
        >
          Got it!
        </Button>
      </ScrollView>
    </View>
  )
}

const themedStyles = StyleService.create({
  topLevelContainer: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  container: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 80,
    paddingBottom: 16,
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
    maxWidth: 210,
    lineHeight: 32,
    marginBottom: 24,
  },
  image: {
    maxWidth: 308,
    height: undefined,
    maxHeight: 164,
    alignSelf: 'center',
    aspectRatio: 3 / 1,
  },
  blurbText: {
    fontWeight: 700,
    marginVertical: 32,
    textAlign: 'center',
    maxWidth: 230,
  },
  desiredWeightLoss: {
    color: 'theme.success.base',
    fontWeight: 700,
    fontSize: 18,
  },
})
